import { useActionsTrack } from "@/_old/features/analytics";
import { useFilters } from "@/_old/features/filters";
import { Group } from "@/company/groups/types";
import { Member } from "@/company/members/types";
import { Button } from "@/core/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/core/components/ui/sheet";
import { formatToISODate } from "@/core/utils";
import { Eraser } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDashboardFilters } from "../store/useDashboardFilters";
import { DashboardFilters } from "../types";
import { DateQuickFilters } from "./dashboard-filters/components/date-filters/components/date-quick-filters";
import { GroupFilter } from "./dashboard-filters/components/group-filter";
import { LocationFilters } from "./dashboard-filters/components/location-filters";
import { MemberFilter } from "./dashboard-filters/components/member-filter";
import { SpaceTypeFilter } from "./dashboard-filters/components/space-type-filter";
import { StatusFilter } from "./dashboard-filters/components/status-filter";

interface DashboardFiltersSheetProps extends React.ComponentPropsWithoutRef<
  typeof Sheet
> {}

export const DashboardFiltersSheet = ({
  ...props
}: DashboardFiltersSheetProps) => {
  const { onActionView } = useActionsTrack();

  useEffect(() => {
    if (props.open) {
      onActionView("DASHBOARD_FILTERS_SHEET_VIEW");
    }
  }, [props.open]);

  const [translate] = useTranslation(["dashboard"]);
  const { dateLimits, groupId } = useFilters();
  const { filters, setFilters, setFilterCount } = useDashboardFilters();
  const [filterCollection, setFilterCollection] =
    useState<DashboardFilters | null>(null);

  const handleChange = useCallback(
    (value: DashboardFilters) => {
      setFilterCollection(value);
    },
    [setFilterCollection],
  );

  const handleSubmit = useCallback(() => {
    if (filterCollection) {
      setFilters(filterCollection as DashboardFilters);
      setFilterCount(
        Object.keys(filterCollection as DashboardFilters).filter(
          (key) =>
            filterCollection[key as keyof DashboardFilters] !== undefined &&
            key !== "datePreset",
        ).length,
      );
      props.onOpenChange?.(false);
    }
  }, [filterCollection, setFilters, props.onOpenChange, setFilterCount]);

  const handleReset = useCallback(() => {
    const start = formatToISODate(dateLimits?.startDate as Date);
    const end = formatToISODate(dateLimits?.endDate as Date);

    setFilters({
      startDate: start,
      endDate: end,
      datePreset: "today",
      group_id: (groupId as number) ?? null,
      type: [],
      status: [],
      users: [],
      cities: [],
      spaces: [],
      neighborhoods: [],
      companyId: null,
    } as DashboardFilters);
    setFilterCount(0);
  }, [setFilters, setFilterCount]);

  return (
    <Sheet {...props}>
      <SheetContent className="overflow-y-auto !px-0 !pb-0">
        <div className="px-4 lg:px-8">
          <SheetHeader>
            <SheetTitle>{translate("dashboard-filters-title")}</SheetTitle>
          </SheetHeader>

          <div className="flex flex-col gap-6 py-4 dashboard-filters-sheet--container">
            <DateQuickFilters
              onSubmit={true}
              onChange={(
                value: {
                  startDate: string;
                  endDate: string;
                  datePreset: string;
                } | null,
              ) =>
                handleChange({
                  ...filterCollection,
                  startDate: value?.startDate as string,
                  endDate: value?.endDate as string,
                  datePreset: value?.datePreset,
                } as DashboardFilters)
              }
            />
            <MemberFilter
              location="dashboard-sheet"
              onSubmit={true}
              onChange={(value: Member[] | null) =>
                handleChange({
                  ...filterCollection,
                  users: value as Member[],
                } as DashboardFilters)
              }
            />
            <SpaceTypeFilter
              location="dashboard-sheet"
              onSubmit={true}
              onChange={(value: string[] | null) =>
                handleChange({
                  ...filterCollection,
                  type: value as string[],
                } as DashboardFilters)
              }
            />
            <LocationFilters
              location="dashboard-sheet"
              onSubmit={true}
              onChange={(value: { id: string; name: string }[] | null) =>
                handleChange({
                  ...filterCollection,
                  cities: value as { id: string; name: string }[],
                } as DashboardFilters)
              }
            />
            <StatusFilter
              location="dashboard-sheet"
              onSubmit={true}
              onChange={(value: number[] | null) =>
                handleChange({
                  ...filterCollection,
                  status: value as number[],
                } as DashboardFilters)
              }
            />
            <GroupFilter
              location="dashboard-sheet"
              onSubmit={true}
              asCheckbox={true}
              onChange={(value: Group | null) =>
                handleChange({
                  ...filterCollection,
                  group_id: value?.id as number,
                } as DashboardFilters)
              }
            />
          </div>
        </div>

        <SheetFooter className="sticky bottom-0 mt-auto border-t border-[#ebebeb] bg-white">
          <div className="flex justify-between items-center px-4 lg:px-8">
            <Button
              variant="link"
              className="px-0 text-danger-500"
              onClick={handleReset}
            >
              <Eraser className="mr-2 w-4 h-4" />
              {translate("dashboard-filters-reset") as string}
            </Button>

            <Button variant="primary" size="lg" onClick={handleSubmit}>
              {translate("dashboard-filters-apply") as string}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
